import { supabase } from '@/lib/supabaseClient';
import { API_URL } from '@/config/api';
import i18n from '@/i18n/i18n';

export interface UserProfile {
    id: string;
    email: string;
    role: string;
    isUpgraded: boolean;
    maxClients: number;
    upgradeExpiresAt: string | null;
    profilePhotoUrl?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    unitSystem?: 'metric' | 'imperial';
}

const getAuthHeader = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`
    };
};

export const userService = {
    getProfile: async (): Promise<UserProfile | null> => {
        try {
            const headers = await getAuthHeader();
            const response = await fetch(`${API_URL}/auth/me`, {
                headers
            });

            if (!response.ok) {
                // If 401, maybe session expired or not logged in
                return null;
            }

            const data = await response.json();
            return data.user;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            return null;
        }
    },

    updatePassword: async (currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user || !user.email) return { success: false, error: 'User not authenticated' };

            // verify current password by signing in
            const { error: signInError } = await supabase.auth.signInWithPassword({
                email: user.email,
                password: currentPassword
            });

            if (signInError) {
                return { success: false, error: i18n.t('settings.securityTab.passwordUpdateError') };
            }

            // update password
            const { error: updateError } = await supabase.auth.updateUser({
                password: newPassword
            });

            if (updateError) {
                return { success: false, error: updateError.message };
            }

            return { success: true };
        } catch (error: any) {
            return { success: false, error: error.message || 'Unknown error' };
        }
    }
};
